/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { Building2, Eye, ExternalLink, FileText, HelpCircle, Image as ImageIcon, MessageSquareQuote, Pencil, Plus, Search, Star, Trash2, Upload, X } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { api } from "../../../services/apiService";
import { Badge } from "../../../components/ops/Badge";
import { CollapsibleRow } from "../../../components/ops/CollapsibleRow";
import { useConfirm } from "../../../components/ops/ConfirmProvider";
import { DataToolbar } from "../../../components/ops/DataToolbar";
import { EmptyState } from "../../../components/ops/EmptyState";
import { Modal } from "../../../components/ops/Modal";
import { PageHeader } from "../../../components/ops/PageHeader";
import { Pagination } from "../../../components/ops/Pagination";
import { SearchSelect } from "../../../components/ops/SearchSelect";
import { useToast } from "../../../components/ops/ToastProvider";
import { usePagedList } from "../../../components/ops/usePagedList";
import { cmsPages } from "../cmsPages";
import { CmsIcon, cmsIconOptions } from "../../../utils/cmsIcons";

const emptyItem = { icon: "", title: "", body: "", value: "", link: "", mediaUrl: "" };

async function uploadMedia(file, accessToken) {
  const baseUrl = process.env.REACT_APP_API_URL || "/api/v1";
  const formData = new FormData();
  formData.append("file", file);
  const response = await fetch(`${baseUrl}/uploads`, {
    method: "POST",
    credentials: "include",
    headers: { Authorization: `Bearer ${accessToken}` },
    body: formData,
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || "Upload failed");
  return data.url;
}

const emptyContentForm = { title: "", subtitle: "", body: "", mediaUrl: "", items: [], seoTitle: "", seoDescription: "", published: false };
const emptyTestimonialForm = { quote: "", author: "", role: "", playerAge: "", outcome: "", rating: 5, published: false };
const emptyFaqForm = { question: "", answer: "", category: "General", order: 0, published: false };
const emptyPartnerForm = { name: "", logoUrl: "", websiteUrl: "", order: 0, published: false };

export function AdminContentPage() {
  const { accessToken } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [tab, setTab] = useState("content");

  const [contentByKey, setContentByKey] = useState({});
  const [selectedPageKey, setSelectedPageKey] = useState(cmsPages[0].key);
  const [selectedSectionKey, setSelectedSectionKey] = useState(cmsPages[0].sections[0].key);
  const [contentSubTab, setContentSubTab] = useState("content");
  const [contentForm, setContentForm] = useState(emptyContentForm);
  const [contentLoading, setContentLoading] = useState(true);
  const [contentSaving, setContentSaving] = useState(false);
  const [mediaUploading, setMediaUploading] = useState(false);
  const [itemMediaUploading, setItemMediaUploading] = useState(null);

  const [testimonials, setTestimonials] = useState([]);
  const [testimonialLoading, setTestimonialLoading] = useState(true);
  const [testimonialForm, setTestimonialForm] = useState(emptyTestimonialForm);
  const [editingTestimonialId, setEditingTestimonialId] = useState(null);
  const [testimonialModalOpen, setTestimonialModalOpen] = useState(false);
  const [testimonialSaving, setTestimonialSaving] = useState(false);

  const [faqs, setFaqs] = useState([]);
  const [faqLoading, setFaqLoading] = useState(true);
  const [faqForm, setFaqForm] = useState(emptyFaqForm);
  const [editingFaqId, setEditingFaqId] = useState(null);
  const [faqModalOpen, setFaqModalOpen] = useState(false);
  const [faqSaving, setFaqSaving] = useState(false);

  const [partners, setPartners] = useState([]);
  const [partnerLoading, setPartnerLoading] = useState(true);
  const [partnerForm, setPartnerForm] = useState(emptyPartnerForm);
  const [editingPartnerId, setEditingPartnerId] = useState(null);
  const [partnerModalOpen, setPartnerModalOpen] = useState(false);
  const [partnerSaving, setPartnerSaving] = useState(false);
  const [partnerLogoUploading, setPartnerLogoUploading] = useState(false);

  const [testimonialSearch, setTestimonialSearch] = useState("");
  const [testimonialStatusFilter, setTestimonialStatusFilter] = useState("all");
  const [faqSearch, setFaqSearch] = useState("");
  const [faqCategoryFilter, setFaqCategoryFilter] = useState("all");
  const [partnerSearch, setPartnerSearch] = useState("");
  const [partnerStatusFilter, setPartnerStatusFilter] = useState("all");

  useEffect(() => {
    setContentLoading(true);
    api.get("/cms/content")
      .then((response) => {
        const map = Object.fromEntries((response.data.content || []).map((c) => [c.key, c]));
        setContentByKey(map);
      })
      .catch(() => toast.error("Unable to load page content"))
      .finally(() => setContentLoading(false));

    setTestimonialLoading(true);
    api.get("/cms/testimonials/manage")
      .then((response) => setTestimonials(response.data.testimonials || []))
      .catch(() => toast.error("Unable to load testimonials"))
      .finally(() => setTestimonialLoading(false));

    setFaqLoading(true);
    api.get("/faq/manage")
      .then((response) => setFaqs(response.data.faqs || []))
      .catch(() => toast.error("Unable to load FAQs"))
      .finally(() => setFaqLoading(false));

    setPartnerLoading(true);
    api.get("/partners/manage")
      .then((response) => setPartners(response.data.partners || []))
      .catch(() => toast.error("Unable to load partners"))
      .finally(() => setPartnerLoading(false));
  }, [accessToken]);

  useEffect(() => {
    const existing = contentByKey[selectedSectionKey];
    setContentForm(existing
      ? {
        title: existing.title || "",
        subtitle: existing.subtitle || "",
        body: existing.body || "",
        mediaUrl: existing.mediaUrl || "",
        items: existing.items?.length ? existing.items : [],
        seoTitle: existing.seoTitle || "",
        seoDescription: existing.seoDescription || "",
        published: !!existing.published,
      }
      : emptyContentForm);
  }, [selectedSectionKey, contentByKey]);

  const activePage = useMemo(() => cmsPages.find((p) => p.key === selectedPageKey), [selectedPageKey]);
  const activeSection = useMemo(() => activePage?.sections.find((s) => s.key === selectedSectionKey), [activePage, selectedSectionKey]);

  const selectPageGroup = (pageKey) => {
    const page = cmsPages.find((p) => p.key === pageKey);
    setSelectedPageKey(pageKey);
    setSelectedSectionKey(page.sections[0].key);
    setContentSubTab("content");
  };

  const selectSection = (key) => {
    setSelectedSectionKey(key);
    setContentSubTab("content");
  };

  const addItem = () => setContentForm((prev) => ({ ...prev, items: [...prev.items, { ...emptyItem }] }));
  const removeItem = (index) => setContentForm((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  const updateItem = (index, field, value) => setContentForm((prev) => ({
    ...prev,
    items: prev.items.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
  }));

  const handleItemMediaUpload = async (index, event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setItemMediaUploading(index);
    try {
      const url = await uploadMedia(file, accessToken);
      updateItem(index, "mediaUrl", url);
      toast.success("Photo uploaded successfully!");
    } catch (err) {
      toast.error(err.message || "Unable to upload photo");
    } finally {
      setItemMediaUploading(null);
      event.target.value = "";
    }
  };

  const filteredTestimonials = useMemo(() => {
    return testimonials.filter((t) => {
      if (testimonialStatusFilter !== "all" && (testimonialStatusFilter === "published") !== Boolean(t.published)) return false;
      return true;
    });
  }, [testimonials, testimonialStatusFilter]);
  const testimonialPages = usePagedList(filteredTestimonials, { search: testimonialSearch, searchFields: ["author", "quote", "role"], pageSize: 6 });

  const faqCategoryOptions = useMemo(() => {
    const unique = [...new Set(faqs.map((f) => f.category).filter(Boolean))].sort();
    return [{ value: "all", label: "All categories" }, ...unique.map((category) => ({ value: category, label: category }))];
  }, [faqs]);
  const filteredFaqs = useMemo(() => {
    return faqs.filter((f) => (faqCategoryFilter === "all" ? true : f.category === faqCategoryFilter));
  }, [faqs, faqCategoryFilter]);
  const faqPages = usePagedList(filteredFaqs, { search: faqSearch, searchFields: ["question", "answer", "category"], pageSize: 8 });

  const filteredPartners = useMemo(() => {
    return partners.filter((p) => {
      if (partnerStatusFilter !== "all" && (partnerStatusFilter === "published") !== Boolean(p.published)) return false;
      return true;
    });
  }, [partners, partnerStatusFilter]);
  const partnerPages = usePagedList(filteredPartners, { search: partnerSearch, searchFields: ["name", "websiteUrl"], pageSize: 8 });

  const handleMediaUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setMediaUploading(true);
    try {
      const url = await uploadMedia(file, accessToken);
      setContentForm((prev) => ({ ...prev, mediaUrl: url }));
      toast.success("File uploaded successfully!");
    } catch (err) {
      toast.error(err.message || "Unable to upload file");
    } finally {
      setMediaUploading(false);
      event.target.value = "";
    }
  };

  const saveContent = async (e) => {
    e.preventDefault();
    setContentSaving(true);
    try {
      const response = await api.put(`/cms/content/${selectedSectionKey}`, contentForm);
      setContentByKey({ ...contentByKey, [selectedSectionKey]: response.data.content });
      toast.success("Content saved successfully!");
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to save content");
    } finally {
      setContentSaving(false);
    }
  };

  const openCreateTestimonial = () => {
    setEditingTestimonialId(null);
    setTestimonialForm(emptyTestimonialForm);
    setTestimonialModalOpen(true);
  };

  const openEditTestimonial = (testimonial) => {
    setEditingTestimonialId(testimonial._id);
    setTestimonialForm({
      quote: testimonial.quote,
      author: testimonial.author,
      role: testimonial.role || "",
      playerAge: testimonial.playerAge || "",
      outcome: testimonial.outcome || "",
      rating: testimonial.rating,
      published: testimonial.published,
    });
    setTestimonialModalOpen(true);
  };

  const closeTestimonialModal = () => {
    setTestimonialModalOpen(false);
    setTestimonialForm(emptyTestimonialForm);
    setEditingTestimonialId(null);
  };

  const submitTestimonial = async (e) => {
    e.preventDefault();
    setTestimonialSaving(true);
    const payload = { ...testimonialForm, rating: Number(testimonialForm.rating) };
    try {
      if (editingTestimonialId) {
        const response = await api.patch(`/cms/testimonials/${editingTestimonialId}`, payload);
        setTestimonials(testimonials.map((t) => (t._id === editingTestimonialId ? response.data.testimonial : t)));
        toast.success("Testimonial updated successfully!");
      } else {
        const response = await api.post("/cms/testimonials", payload);
        setTestimonials([response.data.testimonial, ...testimonials]);
        toast.success("Testimonial created successfully!");
      }
      closeTestimonialModal();
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to save testimonial");
    } finally {
      setTestimonialSaving(false);
    }
  };

  const togglePublished = async (testimonial) => {
    try {
      const response = await api.patch(`/cms/testimonials/${testimonial._id}`, { published: !testimonial.published });
      setTestimonials(testimonials.map((t) => (t._id === testimonial._id ? response.data.testimonial : t)));
      toast.success(response.data.testimonial.published ? "Testimonial published." : "Testimonial unpublished.");
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to update testimonial");
    }
  };

  const removeTestimonial = async (testimonial) => {
    const ok = await confirm({
      title: "Delete this testimonial?",
      message: `The testimonial from "${testimonial.author}" will be permanently removed. This can't be undone.`,
      confirmLabel: "Delete testimonial",
      tone: "danger",
    });
    if (!ok) return;

    try {
      await api.delete(`/cms/testimonials/${testimonial._id}`);
      setTestimonials(testimonials.filter((t) => t._id !== testimonial._id));
      toast.success("Testimonial deleted.");
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to delete testimonial");
    }
  };

  const openCreateFaq = () => {
    setEditingFaqId(null);
    setFaqForm(emptyFaqForm);
    setFaqModalOpen(true);
  };

  const openEditFaq = (faq) => {
    setEditingFaqId(faq._id);
    setFaqForm({ question: faq.question, answer: faq.answer, category: faq.category || "General", order: faq.order || 0, published: faq.published });
    setFaqModalOpen(true);
  };

  const closeFaqModal = () => {
    setFaqModalOpen(false);
    setFaqForm(emptyFaqForm);
    setEditingFaqId(null);
  };

  const submitFaq = async (e) => {
    e.preventDefault();
    setFaqSaving(true);
    const payload = { ...faqForm, order: Number(faqForm.order) || 0 };
    try {
      if (editingFaqId) {
        const response = await api.patch(`/faq/${editingFaqId}`, payload);
        setFaqs(faqs.map((f) => (f._id === editingFaqId ? response.data.faq : f)));
        toast.success("FAQ updated successfully!");
      } else {
        const response = await api.post("/faq", payload);
        setFaqs([...faqs, response.data.faq]);
        toast.success("FAQ created successfully!");
      }
      closeFaqModal();
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to save FAQ");
    } finally {
      setFaqSaving(false);
    }
  };

  const toggleFaqPublished = async (faq) => {
    try {
      const response = await api.patch(`/faq/${faq._id}`, { published: !faq.published });
      setFaqs(faqs.map((f) => (f._id === faq._id ? response.data.faq : f)));
      toast.success(response.data.faq.published ? "FAQ published." : "FAQ unpublished.");
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to update FAQ");
    }
  };

  const removeFaq = async (faq) => {
    const ok = await confirm({
      title: "Delete this FAQ?",
      message: `"${faq.question}" will be permanently removed. This can't be undone.`,
      confirmLabel: "Delete FAQ",
      tone: "danger",
    });
    if (!ok) return;

    try {
      await api.delete(`/faq/${faq._id}`);
      setFaqs(faqs.filter((f) => f._id !== faq._id));
      toast.success("FAQ deleted.");
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to delete FAQ");
    }
  };

  const openCreatePartner = () => {
    setEditingPartnerId(null);
    setPartnerForm(emptyPartnerForm);
    setPartnerModalOpen(true);
  };

  const openEditPartner = (partner) => {
    setEditingPartnerId(partner._id);
    setPartnerForm({
      name: partner.name,
      logoUrl: partner.logoUrl,
      websiteUrl: partner.websiteUrl || "",
      order: partner.order || 0,
      published: partner.published,
    });
    setPartnerModalOpen(true);
  };

  const closePartnerModal = () => {
    setPartnerModalOpen(false);
    setPartnerForm(emptyPartnerForm);
    setEditingPartnerId(null);
  };

  const handlePartnerLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setPartnerLogoUploading(true);
    try {
      const url = await uploadMedia(file, accessToken);
      setPartnerForm((prev) => ({ ...prev, logoUrl: url }));
      toast.success("Logo uploaded successfully!");
    } catch (err) {
      toast.error(err.message || "Unable to upload logo");
    } finally {
      setPartnerLogoUploading(false);
      event.target.value = "";
    }
  };

  const submitPartner = async (e) => {
    e.preventDefault();
    setPartnerSaving(true);
    const payload = { ...partnerForm, order: Number(partnerForm.order) || 0 };
    try {
      if (editingPartnerId) {
        const response = await api.patch(`/partners/${editingPartnerId}`, payload);
        setPartners(partners.map((p) => (p._id === editingPartnerId ? response.data.partner : p)));
        toast.success("Partner updated successfully!");
      } else {
        const response = await api.post("/partners", payload);
        setPartners([...partners, response.data.partner]);
        toast.success("Partner created successfully!");
      }
      closePartnerModal();
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to save partner");
    } finally {
      setPartnerSaving(false);
    }
  };

  const togglePartnerPublished = async (partner) => {
    try {
      const response = await api.patch(`/partners/${partner._id}`, { published: !partner.published });
      setPartners(partners.map((p) => (p._id === partner._id ? response.data.partner : p)));
      toast.success(response.data.partner.published ? "Partner published." : "Partner unpublished.");
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to update partner");
    }
  };

  const removePartner = async (partner) => {
    const ok = await confirm({
      title: "Delete this partner?",
      message: `"${partner.name}" will be permanently removed. This can't be undone.`,
      confirmLabel: "Delete partner",
      tone: "danger",
    });
    if (!ok) return;

    try {
      await api.delete(`/partners/${partner._id}`);
      setPartners(partners.filter((p) => p._id !== partner._id));
      toast.success("Partner deleted.");
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to delete partner");
    }
  };

  return (
    <div>
      <PageHeader title="Content & Testimonials" subtitle="Manage marketing site copy and family testimonials" />

      <div className="ops-chip-row" style={{ marginBottom: 20 }}>
        <button className={`ops-chip ${tab === "content" ? "is-active" : ""}`} onClick={() => setTab("content")}>
          <FileText size={13} /> Page Content
        </button>
        <button className={`ops-chip ${tab === "testimonials" ? "is-active" : ""}`} onClick={() => setTab("testimonials")}>
          <MessageSquareQuote size={13} /> Testimonials
        </button>
        <button className={`ops-chip ${tab === "faq" ? "is-active" : ""}`} onClick={() => setTab("faq")}>
          <HelpCircle size={13} /> FAQs
        </button>
        <button className={`ops-chip ${tab === "partners" ? "is-active" : ""}`} onClick={() => setTab("partners")}>
          <Building2 size={13} /> Partners
        </button>
      </div>

      {tab === "content" && (
        <div className="ops-card">
          <div className="ops-cms-layout">
            <div className="ops-cms-sidebar">
              <p className="ops-cms-sidebar-title">Page</p>
              <select className="ops-select ops-cms-page-picker" value={selectedPageKey} onChange={(e) => selectPageGroup(e.target.value)}>
                {cmsPages.map((page) => <option key={page.key} value={page.key}>{page.label}</option>)}
              </select>

              <p className="ops-cms-sidebar-title">Sections</p>
              {activePage?.sections.map((section) => {
                const record = contentByKey[section.key];
                return (
                  <button
                    key={section.key}
                    type="button"
                    onClick={() => selectSection(section.key)}
                    className={`ops-key-list-item ops-cms-nav-item ${selectedSectionKey === section.key ? "is-active" : ""}`}
                  >
                    <span className={`ops-cms-status-dot ${record?.published ? "is-published" : ""}`} title={record?.published ? "Published" : "Draft"} />
                    <span className="ops-cms-nav-item-copy">
                      <span className="ops-cms-nav-item-label">{section.label}</span>
                      <span className="ops-cms-nav-item-page">{section.type === "items" ? "List section" : "Text section"}</span>
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="ops-cms-editor">
              {contentLoading ? (
                <p className="ops-loading">Loading content...</p>
              ) : (
                <>
                  <div className="ops-cms-editor-header">
                    <div>
                      <p className="ops-cms-breadcrumb">{activePage?.label} page · {activeSection?.type === "items" ? "List section" : "Text section"}</p>
                      <h2 className="ops-cms-editor-title">{activeSection?.label}</h2>
                      <p className="ops-cms-editor-hint">{activeSection?.hint}</p>
                    </div>
                    <div className="ops-cms-editor-header-actions">
                      <a href={activePage?.path} target="_blank" rel="noreferrer" className="ops-button ops-button--outline ops-button--small">
                        <ExternalLink size={13} /> View live page
                      </a>
                      <label className="ops-toggle">
                        <input type="checkbox" checked={contentForm.published} onChange={(e) => setContentForm({ ...contentForm, published: e.target.checked })} />
                        <span className="ops-toggle-track" />
                        <span className="ops-toggle-label">{contentForm.published ? "Published" : "Draft"}</span>
                      </label>
                    </div>
                  </div>

                  <div className="ops-chip-row" style={{ marginTop: 20, marginBottom: 10 }}>
                    <button type="button" className={`ops-chip ${contentSubTab === "content" ? "is-active" : ""}`} onClick={() => setContentSubTab("content")}>
                      <FileText size={13} /> Content
                    </button>
                    <button type="button" className={`ops-chip ${contentSubTab === "seo" ? "is-active" : ""}`} onClick={() => setContentSubTab("seo")}>
                      <Search size={13} /> SEO
                    </button>
                    <button type="button" className={`ops-chip ${contentSubTab === "preview" ? "is-active" : ""}`} onClick={() => setContentSubTab("preview")}>
                      <Eye size={13} /> Preview
                    </button>
                  </div>

                  <form id="content-form" onSubmit={saveContent} className="ops-form ops-cms-form">
                    {contentSubTab === "content" && (
                      <div className="ops-form-section ops-cms-bare-section">
                        {activeSection?.fields.includes("title") && (
                          <div className="ops-form-group">
                            <label className="ops-form-label">Title <span className="ops-form-label-optional">(optional)</span></label>
                            <input className="ops-input" value={contentForm.title} onChange={(e) => setContentForm({ ...contentForm, title: e.target.value })} />
                          </div>
                        )}

                        {activeSection?.fields.includes("subtitle") && (
                          <div className="ops-form-group">
                            <label className="ops-form-label">Subtitle <span className="ops-form-label-optional">(optional)</span></label>
                            <input className="ops-input" value={contentForm.subtitle} onChange={(e) => setContentForm({ ...contentForm, subtitle: e.target.value })} />
                          </div>
                        )}

                        {activeSection?.fields.includes("body") && (
                          <div className="ops-form-group">
                            <label className="ops-form-label">Body</label>
                            <textarea
                              className="ops-textarea"
                              style={{ minHeight: 160 }}
                              placeholder="Separate paragraphs with a blank line..."
                              value={contentForm.body}
                              onChange={(e) => setContentForm({ ...contentForm, body: e.target.value })}
                            />
                            <span className="ops-form-hint">{contentForm.body.length.toLocaleString()} characters · separate paragraphs with a blank line</span>
                          </div>
                        )}

                        {activeSection?.fields.includes("mediaUrl") && (
                          <div className="ops-form-group" style={{ marginBottom: 0 }}>
                            <label className="ops-form-label">Image or video <span className="ops-form-label-optional">(optional)</span></label>
                            <div className="ops-cms-media">
                              <div className="ops-cms-media-thumb">
                                {contentForm.mediaUrl ? (
                                  <img src={contentForm.mediaUrl} alt="" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
                                ) : (
                                  <ImageIcon size={20} strokeWidth={1.5} />
                                )}
                              </div>
                              <div className="ops-cms-media-fields">
                                <input type="url" className="ops-input" placeholder="https://... or upload a file" value={contentForm.mediaUrl} onChange={(e) => setContentForm({ ...contentForm, mediaUrl: e.target.value })} />
                                <div className="ops-cms-media-actions">
                                  <label className="ops-button ops-button--outline ops-button--small" style={{ cursor: "pointer" }}>
                                    <Upload size={13} /> {mediaUploading ? "Uploading..." : "Upload"}
                                    <input type="file" accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime" onChange={handleMediaUpload} disabled={mediaUploading} style={{ display: "none" }} />
                                  </label>
                                  {contentForm.mediaUrl && (
                                    <button type="button" className="ops-button ops-button--outline ops-button--small" onClick={() => setContentForm({ ...contentForm, mediaUrl: "" })}>
                                      <X size={13} /> Remove
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {activeSection?.type === "items" && (
                          <div className="ops-form-group" style={{ marginBottom: 0 }}>
                            <label className="ops-form-label">Items</label>
                            {contentForm.items.length === 0 ? (
                              <EmptyState icon={FileText} title="No items yet" description="Add your first item to this list." />
                            ) : (
                              <div className="ops-cms-item-list">
                                {contentForm.items.map((item, index) => (
                                  <div className="ops-cms-item-row" key={index}>
                                    <div className="ops-cms-item-row-header">
                                      <span className="ops-cms-item-row-index">Item {index + 1}</span>
                                      <button type="button" className="ops-button ops-button--danger ops-button--small" onClick={() => removeItem(index)} aria-label="Remove item">
                                        <Trash2 size={13} />
                                      </button>
                                    </div>
                                    <div className="ops-cms-item-row-fields">
                                      {activeSection.itemFields.includes("mediaUrl") && (
                                        <div className="ops-form-group" style={{ gridColumn: "1 / -1" }}>
                                          <label className="ops-form-label">Photo <span className="ops-form-label-optional">(optional)</span></label>
                                          <div className="ops-cms-media">
                                            <div className="ops-cms-media-thumb">
                                              {item.mediaUrl ? (
                                                <img src={item.mediaUrl} alt="" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
                                              ) : (
                                                <ImageIcon size={20} strokeWidth={1.5} />
                                              )}
                                            </div>
                                            <div className="ops-cms-media-fields">
                                              <input type="url" className="ops-input" placeholder="https://... or upload a file" value={item.mediaUrl} onChange={(e) => updateItem(index, "mediaUrl", e.target.value)} />
                                              <div className="ops-cms-media-actions">
                                                <label className="ops-button ops-button--outline ops-button--small" style={{ cursor: "pointer" }}>
                                                  <Upload size={13} /> {itemMediaUploading === index ? "Uploading..." : "Upload"}
                                                  <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={(e) => handleItemMediaUpload(index, e)} disabled={itemMediaUploading === index} style={{ display: "none" }} />
                                                </label>
                                                {item.mediaUrl && (
                                                  <button type="button" className="ops-button ops-button--outline ops-button--small" onClick={() => updateItem(index, "mediaUrl", "")}>
                                                    <X size={13} /> Remove
                                                  </button>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                      {activeSection.itemFields.includes("icon") && (
                                        <div className="ops-form-group">
                                          <label className="ops-form-label">Icon</label>
                                          <SearchSelect options={cmsIconOptions} value={item.icon} onChange={(value) => updateItem(index, "icon", value)} placeholder="Choose icon" />
                                        </div>
                                      )}
                                      {activeSection.itemFields.includes("value") && (
                                        <div className="ops-form-group">
                                          <label className="ops-form-label">Value / Label</label>
                                          <input className="ops-input" value={item.value} onChange={(e) => updateItem(index, "value", e.target.value)} />
                                        </div>
                                      )}
                                      {activeSection.itemFields.includes("title") && (
                                        <div className="ops-form-group">
                                          <label className="ops-form-label">Title</label>
                                          <input className="ops-input" value={item.title} onChange={(e) => updateItem(index, "title", e.target.value)} />
                                        </div>
                                      )}
                                      {activeSection.itemFields.includes("link") && (
                                        <div className="ops-form-group">
                                          <label className="ops-form-label">Link <span className="ops-form-label-optional">(optional)</span></label>
                                          <input className="ops-input" placeholder="/page or https://..." value={item.link} onChange={(e) => updateItem(index, "link", e.target.value)} />
                                        </div>
                                      )}
                                      {activeSection.itemFields.includes("body") && (
                                        <div className="ops-form-group" style={{ gridColumn: "1 / -1" }}>
                                          <label className="ops-form-label">Description</label>
                                          <textarea className="ops-textarea" style={{ minHeight: 70 }} value={item.body} onChange={(e) => updateItem(index, "body", e.target.value)} />
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            <button type="button" className="ops-cms-item-add" style={{ marginTop: 14, width: "100%" }} onClick={addItem}>
                              <Plus size={14} /> Add item
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {contentSubTab === "seo" && (
                      <div className="ops-form-section ops-cms-bare-section">
                        <div className="ops-form-group">
                          <label className="ops-form-label">SEO title</label>
                          <input maxLength={160} className="ops-input" value={contentForm.seoTitle} onChange={(e) => setContentForm({ ...contentForm, seoTitle: e.target.value })} />
                          <span className="ops-form-hint">{contentForm.seoTitle.length}/160</span>
                        </div>
                        <div className="ops-form-group" style={{ marginBottom: 0 }}>
                          <label className="ops-form-label">SEO description</label>
                          <textarea maxLength={320} className="ops-textarea" style={{ minHeight: 90 }} value={contentForm.seoDescription} onChange={(e) => setContentForm({ ...contentForm, seoDescription: e.target.value })} />
                          <span className="ops-form-hint">{contentForm.seoDescription.length}/320</span>
                        </div>
                      </div>
                    )}

                    {contentSubTab === "preview" && (
                      <div className="ops-cms-preview">
                        <p className="ops-cms-preview-label">Live preview</p>
                        {contentForm.mediaUrl && (
                          <div className="ops-cms-preview-media">
                            <img src={contentForm.mediaUrl} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                          </div>
                        )}
                        {contentForm.title && <h3>{contentForm.title}</h3>}
                        {contentForm.subtitle && <p style={{ fontWeight: 700, color: "var(--ops-ink)", marginTop: 8 }}>{contentForm.subtitle}</p>}
                        {(contentForm.body || "").split("\n\n").filter(Boolean).map((paragraph, index) => (
                          <p key={index} style={{ color: "var(--ops-muted)", lineHeight: 1.8, marginTop: index === 0 && !contentForm.title && !contentForm.subtitle ? 0 : 12 }}>{paragraph}</p>
                        ))}
                        {activeSection?.type === "items" && contentForm.items.length > 0 && (
                          <div className="ops-cms-preview-items">
                            {contentForm.items.map((item, index) => (
                              <div className="ops-cms-preview-item" key={index}>
                                {item.mediaUrl && <img src={item.mediaUrl} alt="" style={{ width: 56, height: 56, borderRadius: "50%", objectFit: "cover", marginBottom: 10 }} onError={(e) => { e.currentTarget.style.display = "none"; }} />}
                                {item.icon && <CmsIcon name={item.icon} size={20} strokeWidth={1.5} className="ops-cms-preview-item-icon" />}
                                {item.value && <p className="ops-cms-preview-item-value">{item.value}</p>}
                                {item.title && <h4>{item.title}</h4>}
                                {item.body && <p>{item.body}</p>}
                              </div>
                            ))}
                          </div>
                        )}
                        {!contentForm.title && !contentForm.subtitle && !contentForm.body && !contentForm.items.length && (
                          <p className="ops-cms-preview-empty">Nothing to preview yet — add content under the Content tab.</p>
                        )}
                      </div>
                    )}

                    <div className="ops-form-actions">
                      <button type="submit" className="ops-button ops-button--primary" disabled={contentSaving}>
                        {contentSaving ? "Saving..." : "Save Content"}
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === "testimonials" && (
        <div className="ops-card">
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
            <button type="button" className="ops-button ops-button--primary" onClick={openCreateTestimonial}>
              <Plus size={16} /> New Testimonial
            </button>
          </div>

          <DataToolbar
            search={testimonialSearch}
            onSearchChange={setTestimonialSearch}
            searchPlaceholder="Search by author or quote..."
            filters={[
              {
                label: "Status",
                value: testimonialStatusFilter,
                onChange: setTestimonialStatusFilter,
                options: [{ value: "all", label: "All statuses" }, { value: "published", label: "Published" }, { value: "draft", label: "Draft" }],
              },
            ]}
            resultCount={testimonialPages.totalItems}
            itemLabel="testimonials"
          />
          {testimonialLoading ? (
            <p className="ops-loading">Loading testimonials...</p>
          ) : testimonialPages.totalItems === 0 ? (
            <EmptyState icon={MessageSquareQuote} title="No testimonials found" description={testimonials.length === 0 ? "Add your first family testimonial to feature it on the homepage." : "Try adjusting your search or filters."} />
          ) : (
            <>
              <div className="ops-cards-grid">
                {testimonialPages.pageItems.map((testimonial) => (
                  <div key={testimonial._id} className="ops-entity-card">
                    <div className="ops-entity-card-header">
                      <div className="ops-entity-card-icon"><MessageSquareQuote size={20} /></div>
                      <div className="ops-entity-card-heading">
                        <h3 className="ops-entity-card-title">{testimonial.author}</h3>
                        <p className="ops-entity-card-subtitle">{testimonial.role || "Formula Soccer family"}</p>
                      </div>
                      <Badge tone={testimonial.published ? "success" : "neutral"}>{testimonial.published ? "Published" : "Draft"}</Badge>
                    </div>
                    <div className="ops-entity-card-body">
                      <p style={{ display: "flex", gap: 2, marginBottom: 8 }}>
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star key={i} size={13} fill={i < testimonial.rating ? "var(--ops-accent-ink)" : "none"} stroke="var(--ops-accent-ink)" strokeWidth={1.5} />
                        ))}
                      </p>
                      <p>&ldquo;{testimonial.quote}&rdquo;</p>
                    </div>
                    {(testimonial.playerAge || testimonial.outcome) && (
                      <div className="ops-entity-card-tags">
                        {testimonial.playerAge && <span className="ops-entity-card-tag">Age {testimonial.playerAge}</span>}
                        {testimonial.outcome && <span className="ops-entity-card-tag">{testimonial.outcome}</span>}
                      </div>
                    )}
                    <div className="ops-entity-card-actions">
                      <button className="ops-button ops-button--outline ops-button--small" onClick={() => openEditTestimonial(testimonial)}>
                        <Pencil size={13} /> Edit
                      </button>
                      <button className="ops-button ops-button--outline ops-button--small" onClick={() => togglePublished(testimonial)}>
                        {testimonial.published ? "Unpublish" : "Publish"}
                      </button>
                      <button className="ops-button ops-button--danger ops-button--small" onClick={() => removeTestimonial(testimonial)} aria-label="Delete testimonial">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <Pagination page={testimonialPages.page} totalPages={testimonialPages.totalPages} onPageChange={testimonialPages.setPage} />
            </>
          )}
        </div>
      )}

      {tab === "faq" && (
        <div className="ops-card">
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
            <button type="button" className="ops-button ops-button--primary" onClick={openCreateFaq}>
              <Plus size={16} /> New FAQ
            </button>
          </div>

          <DataToolbar
            search={faqSearch}
            onSearchChange={setFaqSearch}
            searchPlaceholder="Search by question or answer..."
            filters={[{ label: "Category", value: faqCategoryFilter, onChange: setFaqCategoryFilter, options: faqCategoryOptions }]}
            resultCount={faqPages.totalItems}
            itemLabel="FAQs"
          />
          {faqLoading ? (
            <p className="ops-loading">Loading FAQs...</p>
          ) : faqPages.totalItems === 0 ? (
            <EmptyState icon={HelpCircle} title="No FAQs found" description={faqs.length === 0 ? "Add your first question to feature it on the FAQ page." : "Try adjusting your search or filters."} />
          ) : (
            <>
              <ul className="ops-list">
                {faqPages.pageItems.map((faq) => (
                  <CollapsibleRow
                    key={faq._id}
                    avatar={<HelpCircle size={16} />}
                    title={<>{faq.question} <Badge tone="neutral">{faq.category}</Badge></>}
                    meta={`Order ${faq.order ?? 0}`}
                    trailing={
                      <>
                        <Badge tone={faq.published ? "success" : "neutral"}>{faq.published ? "Published" : "Draft"}</Badge>
                        <button className="ops-button ops-button--outline ops-button--small" onClick={() => openEditFaq(faq)}>
                          <Pencil size={13} /> Edit
                        </button>
                        <button className="ops-button ops-button--outline ops-button--small" onClick={() => toggleFaqPublished(faq)}>
                          {faq.published ? "Unpublish" : "Publish"}
                        </button>
                        <button className="ops-button ops-button--danger ops-button--small" onClick={() => removeFaq(faq)} aria-label="Delete FAQ">
                          <Trash2 size={13} />
                        </button>
                      </>
                    }
                  >
                    <p>{faq.answer}</p>
                  </CollapsibleRow>
                ))}
              </ul>
              <Pagination page={faqPages.page} totalPages={faqPages.totalPages} onPageChange={faqPages.setPage} />
            </>
          )}
        </div>
      )}

      {tab === "partners" && (
        <div className="ops-card">
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
            <button type="button" className="ops-button ops-button--primary" onClick={openCreatePartner}>
              <Plus size={16} /> New Partner
            </button>
          </div>

          <DataToolbar
            search={partnerSearch}
            onSearchChange={setPartnerSearch}
            searchPlaceholder="Search by partner name..."
            filters={[
              {
                label: "Status",
                value: partnerStatusFilter,
                onChange: setPartnerStatusFilter,
                options: [{ value: "all", label: "All statuses" }, { value: "published", label: "Published" }, { value: "draft", label: "Draft" }],
              },
            ]}
            resultCount={partnerPages.totalItems}
            itemLabel="partners"
          />
          {partnerLoading ? (
            <p className="ops-loading">Loading partners...</p>
          ) : partnerPages.totalItems === 0 ? (
            <EmptyState icon={Building2} title="No partners found" description={partners.length === 0 ? "Add a partner or club logo to feature it on the homepage." : "Try adjusting your search or filters."} />
          ) : (
            <>
              <ul className="ops-list">
                {partnerPages.pageItems.map((partner) => (
                  <li key={partner._id} className="ops-list-item">
                    <div className="ops-list-item-avatar" style={{ background: "var(--ops-surface-soft)", padding: 4 }}>
                      <img src={partner.logoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                    </div>
                    <div className="ops-list-item-body">
                      <p className="ops-list-item-title">{partner.name}</p>
                      <p className="ops-list-item-meta">{partner.websiteUrl || "No website link"}</p>
                    </div>
                    <div className="ops-list-item-trailing">
                      <Badge tone={partner.published ? "success" : "neutral"}>{partner.published ? "Published" : "Draft"}</Badge>
                      <button className="ops-button ops-button--outline ops-button--small" onClick={() => openEditPartner(partner)}>
                        <Pencil size={13} /> Edit
                      </button>
                      <button className="ops-button ops-button--outline ops-button--small" onClick={() => togglePartnerPublished(partner)}>
                        {partner.published ? "Unpublish" : "Publish"}
                      </button>
                      <button className="ops-button ops-button--danger ops-button--small" onClick={() => removePartner(partner)} aria-label="Delete partner">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
              <Pagination page={partnerPages.page} totalPages={partnerPages.totalPages} onPageChange={partnerPages.setPage} />
            </>
          )}
        </div>
      )}

      <Modal
        open={partnerModalOpen}
        onClose={closePartnerModal}
        title={editingPartnerId ? "Edit Partner" : "New Partner"}
        size="md"
        footer={
          <>
            <button type="submit" form="partner-form" className="ops-button ops-button--primary" disabled={partnerSaving}>
              {partnerSaving ? "Saving..." : editingPartnerId ? "Save Changes" : "Create Partner"}
            </button>
            <button type="button" className="ops-button ops-button--secondary" onClick={closePartnerModal}>Cancel</button>
          </>
        }
      >
        <form id="partner-form" onSubmit={submitPartner} className="ops-form">
          <div className="ops-form-section">
            <p className="ops-form-section-title">Partner details</p>
            <div className="ops-form-group">
              <label className="ops-form-label">Name</label>
              <input required className="ops-input" placeholder="e.g., Adidas" value={partnerForm.name} onChange={(e) => setPartnerForm({ ...partnerForm, name: e.target.value })} />
            </div>
            <div className="ops-form-group">
              <label className="ops-form-label">Logo</label>
              <div style={{ display: "flex", gap: 10 }}>
                <input required type="url" className="ops-input" placeholder="https://... or upload a file" value={partnerForm.logoUrl} onChange={(e) => setPartnerForm({ ...partnerForm, logoUrl: e.target.value })} />
                <label className="ops-button ops-button--outline ops-button--small" style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                  <Upload size={13} /> {partnerLogoUploading ? "Uploading..." : "Upload"}
                  <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={handlePartnerLogoUpload} disabled={partnerLogoUploading} style={{ display: "none" }} />
                </label>
              </div>
            </div>
            <div className="ops-form-row">
              <div>
                <label className="ops-form-label">Website URL <span className="ops-form-label-optional">(optional)</span></label>
                <input type="url" className="ops-input" placeholder="https://..." value={partnerForm.websiteUrl} onChange={(e) => setPartnerForm({ ...partnerForm, websiteUrl: e.target.value })} />
              </div>
              <div>
                <label className="ops-form-label">Display order</label>
                <input type="number" className="ops-input" value={partnerForm.order} onChange={(e) => setPartnerForm({ ...partnerForm, order: e.target.value })} />
              </div>
            </div>
          </div>

          <div className="ops-form-section" style={{ borderBottom: "none", paddingBottom: 0, marginBottom: 0 }}>
            <label className="ops-toggle">
              <input type="checkbox" checked={partnerForm.published} onChange={(e) => setPartnerForm({ ...partnerForm, published: e.target.checked })} />
              <span className="ops-toggle-track" />
              <span className="ops-toggle-label">Published</span>
            </label>
          </div>

        </form>
      </Modal>

      <Modal
        open={faqModalOpen}
        onClose={closeFaqModal}
        title={editingFaqId ? "Edit FAQ" : "New FAQ"}
        size="md"
        footer={
          <>
            <button type="submit" form="faq-form" className="ops-button ops-button--primary" disabled={faqSaving}>
              {faqSaving ? "Saving..." : editingFaqId ? "Save Changes" : "Create FAQ"}
            </button>
            <button type="button" className="ops-button ops-button--secondary" onClick={closeFaqModal}>Cancel</button>
          </>
        }
      >
        <form id="faq-form" onSubmit={submitFaq} className="ops-form">
          <div className="ops-form-section">
            <p className="ops-form-section-title">Question & answer</p>
            <div className="ops-form-group">
              <label className="ops-form-label">Question</label>
              <input required className="ops-input" value={faqForm.question} onChange={(e) => setFaqForm({ ...faqForm, question: e.target.value })} />
            </div>
            <div className="ops-form-group">
              <label className="ops-form-label">Answer</label>
              <textarea required className="ops-textarea" value={faqForm.answer} onChange={(e) => setFaqForm({ ...faqForm, answer: e.target.value })} />
            </div>
            <div className="ops-form-row">
              <div>
                <label className="ops-form-label">Category</label>
                <input className="ops-input" placeholder="e.g., Memberships" value={faqForm.category} onChange={(e) => setFaqForm({ ...faqForm, category: e.target.value })} />
              </div>
              <div>
                <label className="ops-form-label">Display order</label>
                <input type="number" className="ops-input" value={faqForm.order} onChange={(e) => setFaqForm({ ...faqForm, order: e.target.value })} />
              </div>
            </div>
          </div>

          <div className="ops-form-section" style={{ borderBottom: "none", paddingBottom: 0, marginBottom: 0 }}>
            <label className="ops-toggle">
              <input type="checkbox" checked={faqForm.published} onChange={(e) => setFaqForm({ ...faqForm, published: e.target.checked })} />
              <span className="ops-toggle-track" />
              <span className="ops-toggle-label">Published</span>
            </label>
          </div>

        </form>
      </Modal>

      <Modal
        open={testimonialModalOpen}
        onClose={closeTestimonialModal}
        title={editingTestimonialId ? "Edit Testimonial" : "New Testimonial"}
        size="md"
        footer={
          <>
            <button type="submit" form="testimonial-form" className="ops-button ops-button--primary" disabled={testimonialSaving}>
              {testimonialSaving ? "Saving..." : editingTestimonialId ? "Save Changes" : "Create Testimonial"}
            </button>
            <button type="button" className="ops-button ops-button--secondary" onClick={closeTestimonialModal}>Cancel</button>
          </>
        }
      >
        <form id="testimonial-form" onSubmit={submitTestimonial} className="ops-form">
          <div className="ops-form-section">
            <p className="ops-form-section-title">Testimonial</p>
            <div className="ops-form-group">
              <label className="ops-form-label">Quote</label>
              <textarea required className="ops-textarea" value={testimonialForm.quote} onChange={(e) => setTestimonialForm({ ...testimonialForm, quote: e.target.value })} />
            </div>
            <div className="ops-form-row">
              <div>
                <label className="ops-form-label">Author</label>
                <input required className="ops-input" placeholder="Parent's name" value={testimonialForm.author} onChange={(e) => setTestimonialForm({ ...testimonialForm, author: e.target.value })} />
              </div>
              <div>
                <label className="ops-form-label">Role <span className="ops-form-label-optional">(optional)</span></label>
                <input className="ops-input" placeholder="e.g., Parent of a U11 player" value={testimonialForm.role} onChange={(e) => setTestimonialForm({ ...testimonialForm, role: e.target.value })} />
              </div>
            </div>
          </div>

          <div className="ops-form-section">
            <p className="ops-form-section-title">Outcome <span className="ops-form-label-optional">(optional)</span></p>
            <div className="ops-form-row">
              <div>
                <label className="ops-form-label">Player age</label>
                <input className="ops-input" placeholder="e.g., 11" value={testimonialForm.playerAge} onChange={(e) => setTestimonialForm({ ...testimonialForm, playerAge: e.target.value })} />
              </div>
              <div>
                <label className="ops-form-label">Measurable outcome</label>
                <input className="ops-input" placeholder="e.g., FPI +18 in 3 months" value={testimonialForm.outcome} onChange={(e) => setTestimonialForm({ ...testimonialForm, outcome: e.target.value })} />
              </div>
            </div>
          </div>

          <div className="ops-form-section" style={{ display: "flex", alignItems: "center", gap: 24 }}>
            <div>
              <label className="ops-form-label">Rating</label>
              <select className="ops-select" value={testimonialForm.rating} onChange={(e) => setTestimonialForm({ ...testimonialForm, rating: e.target.value })}>
                {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} star{n !== 1 ? "s" : ""}</option>)}
              </select>
            </div>
            <label className="ops-toggle" style={{ marginTop: 22 }}>
              <input type="checkbox" checked={testimonialForm.published} onChange={(e) => setTestimonialForm({ ...testimonialForm, published: e.target.checked })} />
              <span className="ops-toggle-track" />
              <span className="ops-toggle-label">Published</span>
            </label>
          </div>

        </form>
      </Modal>
    </div>
  );
}
